import React, { useState, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { useCart, getEffectiveUnitPrice } from '../context/CartContext';

const CATEGORY_TABS = [
  { id: 'all', label: 'All Products', icon: '🎾' },
  { id: 'bundles', label: 'Bundles', icon: '🏆' },
  { id: 'books', label: 'Books', icon: '📘' },
  { id: 'logbooks', label: 'Logbooks', icon: '📓' },
  { id: 'accessories', label: 'Pro Accessories', icon: '🖊️' },
  { id: 'gift_cards', label: 'Gift Cards & Digital', icon: '🎁' }
];

const ExtrasShopPage = () => {
  const {
    products,
    settings,
    addToCart,
    cartCount,
    setIsCartOpen,
    formatPrice,
    activeTaxZone
  } = useCart();

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('featured');
  const [selectedVariants, setSelectedVariants] = useState({});
  const [activeCardImage, setActiveCardImage] = useState({});
  const [quickViewProduct, setQuickViewProduct] = useState(null);
  const [quickViewImageIdx, setQuickViewImageIdx] = useState(0);
  const [quickViewQty, setQuickViewQty] = useState(1);
  const [addedFeedbackId, setAddedFeedbackId] = useState(null);

  const getProductVariant = (product) => {
    if (selectedVariants[product.id]) {
      return selectedVariants[product.id];
    }
    if (
      Array.isArray(product.variants) &&
      product.variants.length > 0 &&
      Array.isArray(product.variants[0].options) &&
      product.variants[0].options.length > 0
    ) {
      return product.variants[0].options[0];
    }
    return '';
  };

  const handleVariantChange = (productId, optionValue) => {
    setSelectedVariants((prev) => ({
      ...prev,
      [productId]: optionValue
    }));
  };

  const handleAddProduct = (product, qty = 1, openDrawer = true) => {
    const variant = getProductVariant(product);
    addToCart(product, qty, variant, openDrawer);
    setAddedFeedbackId(product.id);
    setTimeout(() => {
      setAddedFeedbackId((prev) => (prev === product.id ? null : prev));
    }, 1800);
  };

  const featuredProduct = useMemo(
    () => products.find((p) => p.is_featured && p.is_active) || products[0] || null,
    [products]
  );

  const filteredProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const list = products.filter((p) => {
      if (!p.is_active) return false;
      if (selectedCategory !== 'all') {
        if (selectedCategory === 'gift_cards') {
          if (p.category !== 'gift_cards' && p.product_type !== 'digital') return false;
        } else if (p.category !== selectedCategory) {
          return false;
        }
      }
      if (q) {
        const hay = `${p.name} ${p.short_description} ${p.description} ${p.category} ${p.sku}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    return [...list].sort((a, b) => {
      if (sortBy === 'price-asc') return a.price - b.price;
      if (sortBy === 'price-desc') return b.price - a.price;
      if (sortBy === 'name-asc') return a.name.localeCompare(b.name);
      // default: featured & sort_order
      if (a.is_featured !== b.is_featured) return a.is_featured ? -1 : 1;
      return (a.sort_order || 0) - (b.sort_order || 0);
    });
  }, [products, selectedCategory, searchQuery, sortBy]);

  const openQuickView = (product) => {
    setQuickViewProduct(product);
    setQuickViewImageIdx(0);
    setQuickViewQty(1);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50/60 via-white to-yellow-50/40" data-testid="shop-page">
      {/* Top Store Notice Bar */}
      <div className="bg-green-900 text-white text-xs sm:text-sm py-2.5 px-4 border-b border-green-800">
        <div className="container mx-auto flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 font-medium">
            <span className="inline-block px-2 py-0.5 rounded bg-yellow-400 text-green-950 font-extrabold text-xs">
              PRO SHOP
            </span>
            <span>
              {settings.store_notice || 'Free standard shipping on physical orders over $50 CAD!'}
            </span>
          </div>
          <div className="flex items-center gap-4 text-xs">
            <span className="text-green-200 hidden sm:inline">
              Currency: <strong className="text-white">{settings.default_currency || 'CAD'}</strong> • Tax:{' '}
              <strong className="text-white">
                {activeTaxZone?.tax_name || 'HST'} ({activeTaxZone?.tax_rate ?? 13}%)
              </strong>
            </span>
            <NavLink
              to="/track-order"
              className="text-yellow-300 hover:text-yellow-200 font-semibold underline underline-offset-2"
              data-testid="shop-track-order-link"
            >
              📦 Track Order
            </NavLink>
          </div>
        </div>
      </div>

      {/* Hero & Featured Bundle Section */}
      <section className="bg-gradient-to-r from-green-100 via-green-50 to-yellow-100 py-10 sm:py-14 border-b border-green-200/70">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8 max-w-6xl mx-auto">
            {/* Left Copy */}
            <div className="flex-1 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-green-800 text-yellow-300 text-xs font-extrabold uppercase tracking-wider px-3.5 py-1.5 rounded-full mb-4 shadow-xs">
                <span>🏆 Official Best3inTennis Storefront</span>
              </div>
              <h1 className="text-3xl sm:text-5xl font-extrabold text-green-900 leading-tight mb-4">
                The Pro Player Shop
              </h1>
              <p className="text-base sm:text-lg text-gray-700 mb-6 max-w-xl mx-auto lg:mx-0">
                Equip your tennis bag with court-side match &amp; practice logbooks, mental toughness training guides,
                pro racquet pens, and strategy posters—with fast, secure PayPal checkout.
              </p>
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3">
                <a
                  href="#catalog-grid"
                  className="bg-green-700 hover:bg-green-800 text-white font-bold py-3 px-6 rounded-xl shadow-md transition duration-200"
                >
                  Browse All Gear
                </a>
                <button
                  type="button"
                  onClick={() => setIsCartOpen(true)}
                  className="bg-white hover:bg-yellow-50 text-green-900 border border-green-300 font-bold py-3 px-5 rounded-xl shadow-xs transition duration-200 flex items-center gap-2 cursor-pointer"
                  data-testid="hero-open-cart-btn"
                >
                  <span>🛒 View Cart ({cartCount})</span>
                </button>
              </div>
            </div>

            {/* Featured Bundle Spotlight Card */}
            {featuredProduct && (
              <div
                className="flex-1 w-full max-w-xl bg-white rounded-2xl shadow-xl border-2 border-yellow-400 overflow-hidden"
                data-testid="featured-bundle-card"
              >
                <div className="bg-gradient-to-r from-green-800 to-green-700 text-white px-5 py-2.5 flex items-center justify-between">
                  <span className="text-xs font-extrabold uppercase tracking-wider text-yellow-300">
                    ⭐ Featured Player Bundle
                  </span>
                  {featuredProduct.compare_at_price && featuredProduct.compare_at_price > featuredProduct.price && (
                    <span className="bg-yellow-400 text-green-950 text-xs font-extrabold px-2.5 py-0.5 rounded-full">
                      Save {formatPrice(featuredProduct.compare_at_price - featuredProduct.price)}
                    </span>
                  )}
                </div>
                <div className="p-5 sm:p-6 flex flex-col sm:flex-row gap-5 items-center">
                  <div
                    className="w-full sm:w-44 h-44 rounded-xl overflow-hidden bg-green-50 border border-gray-200 flex-shrink-0 cursor-pointer relative group"
                    onClick={() => openQuickView(featuredProduct)}
                  >
                    <img
                      src={featuredProduct.primary_image_url}
                      alt={featuredProduct.name}
                      fetchpriority="high"
                      width="320"
                      height="240"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <span className="absolute bottom-2 right-2 bg-black/70 text-white text-[11px] font-semibold px-2 py-0.5 rounded">
                      🔍 View Gallery
                    </span>
                  </div>
                  <div className="flex-1 text-left">
                    <h2 className="text-xl font-extrabold text-green-900 mb-1.5">{featuredProduct.name}</h2>
                    <p className="text-xs sm:text-sm text-gray-600 mb-3 line-clamp-2">
                      {featuredProduct.short_description}
                    </p>
                    <div className="flex items-baseline gap-2 mb-4">
                      <span className="text-2xl font-extrabold text-green-800">
                        {formatPrice(getEffectiveUnitPrice(featuredProduct, getProductVariant(featuredProduct)))}
                      </span>
                      {featuredProduct.compare_at_price && (
                        <span className="text-sm text-gray-400 line-through">
                          {formatPrice(featuredProduct.compare_at_price)}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => handleAddProduct(featuredProduct, 1, true)}
                        className="flex-1 bg-yellow-400 hover:bg-yellow-500 text-green-950 font-extrabold py-2.5 px-4 rounded-xl text-sm shadow-sm transition cursor-pointer"
                        data-testid="add-featured-bundle-btn"
                      >
                        {addedFeedbackId === featuredProduct.id ? '✓ Added to Cart!' : 'Add Bundle to Cart'}
                      </button>
                      <button
                        type="button"
                        onClick={() => openQuickView(featuredProduct)}
                        className="px-3.5 py-2.5 rounded-xl border border-gray-300 hover:bg-gray-50 text-gray-700 font-semibold text-sm transition cursor-pointer"
                      >
                        Details
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Filter, Search & Sort Controls */}
      <section id="catalog-grid" className="py-10 container mx-auto px-4">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-5 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            {/* Category Tabs */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1 lg:pb-0" role="tablist">
              {CATEGORY_TABS.map((tab) => {
                const active = selectedCategory === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setSelectedCategory(tab.id)}
                    data-testid={`category-tab-${tab.id}`}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs sm:text-sm font-bold whitespace-nowrap transition cursor-pointer ${
                      active
                        ? 'bg-green-700 text-white shadow-xs'
                        : 'bg-gray-100 hover:bg-green-50 text-gray-700'
                    }`}
                  >
                    <span>{tab.icon}</span>
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Search & Sort */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <div className="relative flex-1 sm:w-64">
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search pens, books, posters..."
                  aria-label="Search shop products"
                  data-testid="shop-search-input"
                  className="w-full pl-9 pr-3 py-2 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-600 text-gray-800"
                />
                <span className="absolute left-3 top-2.5 text-gray-400 text-sm" aria-hidden="true">
                  🔍
                </span>
              </div>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                aria-label="Sort products"
                data-testid="shop-sort-select"
                className="px-3.5 py-2 rounded-xl border border-gray-300 text-sm font-medium text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-green-600"
              >
                <option value="featured">Sort: Featured</option>
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
                <option value="name-asc">Name: A to Z</option>
              </select>
            </div>
          </div>
        </div>

        {/* Product Grid */}
        {filteredProducts.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
            <p className="text-lg font-bold text-gray-800 mb-2">No matching products found</p>
            <p className="text-sm text-gray-500 mb-4">
              Try clearing your search filter or switching product categories.
            </p>
            <button
              type="button"
              onClick={() => {
                setSelectedCategory('all');
                setSearchQuery('');
              }}
              className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-5 rounded-lg text-sm cursor-pointer"
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7" data-testid="products-grid">
            {filteredProducts.map((product) => {
              const currentVariant = getProductVariant(product);
              const unitPrice = getEffectiveUnitPrice(product, currentVariant);
              const gallery =
                Array.isArray(product.gallery_images) && product.gallery_images.length > 0
                  ? product.gallery_images
                  : [product.primary_image_url];
              const displayedImg = activeCardImage[product.id] || product.primary_image_url;
              const outOfStock = product.track_inventory && product.stock_quantity <= 0;

              return (
                <div
                  key={product.id}
                  data-testid={`product-card-${product.id}`}
                  className="bg-white rounded-2xl shadow-md hover:shadow-xl border border-gray-200/90 overflow-hidden flex flex-col justify-between transition-shadow duration-200"
                >
                  <div>
                    {/* Product Image Header */}
                    <div className="relative h-64 bg-gradient-to-br from-green-50 to-yellow-50/50 border-b border-gray-100 overflow-hidden group">
                      <img
                        src={displayedImg}
                        alt={product.name}
                        loading="lazy"
                        width="400"
                        height="300"
                        onClick={() => openQuickView(product)}
                        className="w-full h-full object-contain p-3 cursor-pointer group-hover:scale-105 transition-transform duration-300"
                      />

                      {/* Top Badges */}
                      <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                        {product.is_featured && (
                          <span className="bg-yellow-400 text-green-950 text-[11px] font-extrabold uppercase px-2.5 py-0.5 rounded-full shadow-xs">
                            ★ Best Seller
                          </span>
                        )}
                        <span
                          className={`text-[11px] font-bold uppercase px-2.5 py-0.5 rounded-full shadow-xs ${
                            product.product_type === 'digital'
                              ? 'bg-blue-600 text-white'
                              : 'bg-green-800 text-white'
                          }`}
                        >
                          {product.product_type === 'digital' ? '⚡ Instant Digital' : '📦 Physical Gear'}
                        </span>
                      </div>

                      {/* Quick View Button */}
                      <button
                        type="button"
                        onClick={() => openQuickView(product)}
                        data-testid={`quick-view-btn-${product.id}`}
                        className="absolute bottom-3 right-3 bg-white/95 hover:bg-white text-green-900 text-xs font-bold px-3 py-1.5 rounded-lg shadow-md border border-gray-200 transition cursor-pointer"
                      >
                        🖼️ Photos ({gallery.length})
                      </button>
                    </div>

                    {/* Multi-Image Thumbnail Strip */}
                    {gallery.length > 1 && (
                      <div className="px-5 pt-3 flex items-center gap-2 overflow-x-auto">
                        {gallery.map((imgUrl, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() =>
                              setActiveCardImage((prev) => ({
                                ...prev,
                                [product.id]: imgUrl
                              }))
                            }
                            className={`w-11 h-11 rounded-lg overflow-hidden border-2 flex-shrink-0 cursor-pointer transition ${
                              displayedImg === imgUrl
                                ? 'border-green-600 ring-1 ring-green-500'
                                : 'border-gray-200 opacity-75 hover:opacity-100'
                            }`}
                            aria-label={`Preview image ${idx + 1} for ${product.name}`}
                          >
                            <img
                              src={imgUrl}
                              alt=""
                              loading="lazy"
                              fetchpriority="low"
                              className="w-full h-full object-cover"
                            />
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Card Details */}
                    <div className="p-5">
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                        <span className="uppercase tracking-wider font-semibold text-green-700">
                          {product.category.replace('_', ' ')}
                        </span>
                        {product.track_inventory ? (
                          <span
                            className={
                              outOfStock
                                ? 'text-red-600 font-bold'
                                : product.stock_quantity <= 10
                                ? 'text-amber-600 font-bold'
                                : 'text-green-700 font-medium'
                            }
                          >
                            {outOfStock ? 'Out of Stock' : `✓ In Stock (${product.stock_quantity})`}
                          </span>
                        ) : (
                          <span className="text-green-700 font-medium">✓ Instant Delivery</span>
                        )}
                      </div>

                      <h3
                        onClick={() => openQuickView(product)}
                        className="text-lg font-extrabold text-gray-900 hover:text-green-800 cursor-pointer mb-2"
                      >
                        {product.name}
                      </h3>

                      <p className="text-sm text-gray-600 mb-4 line-clamp-2">{product.short_description}</p>

                      {/* Variant Selector */}
                      {Array.isArray(product.variants) &&
                        product.variants.map((variantGroup, vIdx) => (
                          <div key={vIdx} className="mb-4">
                            <label className="block text-xs font-bold text-gray-700 mb-1.5">
                              {variantGroup.name}:
                            </label>
                            <div className="flex flex-wrap gap-1.5">
                              {(variantGroup.options || []).map((opt) => {
                                const isSelected = currentVariant === opt;
                                return (
                                  <button
                                    key={opt}
                                    type="button"
                                    onClick={() => handleVariantChange(product.id, opt)}
                                    data-testid={`variant-${product.id}-${opt}`}
                                    className={`text-xs px-2.5 py-1 rounded-lg font-semibold border transition cursor-pointer ${
                                      isSelected
                                        ? 'bg-green-700 text-white border-green-700 shadow-2xs'
                                        : 'bg-gray-50 text-gray-700 border-gray-300 hover:border-green-500'
                                    }`}
                                  >
                                    {opt}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>

                  {/* Card Footer: Price + Actions */}
                  <div className="px-5 pb-5 pt-3 border-t border-gray-100 bg-gray-50/50">
                    <div className="flex items-baseline justify-between mb-3">
                      <div>
                        <span
                          className="text-2xl font-extrabold text-green-900"
                          data-testid={`product-price-${product.id}`}
                        >
                          {formatPrice(unitPrice)}
                        </span>
                        {product.compare_at_price && product.compare_at_price > unitPrice && (
                          <span className="ml-2 text-xs text-gray-400 line-through">
                            {formatPrice(product.compare_at_price)}
                          </span>
                        )}
                      </div>
                      <span className="text-[11px] text-gray-500">
                        + {activeTaxZone?.tax_name || 'HST'} ({activeTaxZone?.tax_rate ?? 13}%)
                      </span>
                    </div>

                    <div className="flex flex-col gap-2">
                      {product.product_type !== 'external' && (
                        <button
                          type="button"
                          disabled={outOfStock}
                          onClick={() => handleAddProduct(product, 1, true)}
                          data-testid={`add-to-cart-${product.id}`}
                          className={`w-full py-2.5 px-4 rounded-xl font-extrabold text-sm shadow-xs transition flex items-center justify-center gap-2 cursor-pointer ${
                            outOfStock
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              : addedFeedbackId === product.id
                              ? 'bg-green-800 text-yellow-300'
                              : 'bg-green-600 hover:bg-green-700 text-white'
                          }`}
                        >
                          {outOfStock ? (
                            'Sold Out'
                          ) : addedFeedbackId === product.id ? (
                            <>✓ Added to Cart</>
                          ) : (
                            <>
                              <span>🛒</span>
                              <span>Add to Cart</span>
                            </>
                          )}
                        </button>
                      )}

                      {product.external_url && (
                        <a
                          href={product.external_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`external-buy-${product.id}`}
                          className="w-full py-2 px-4 rounded-xl font-bold text-xs text-center border border-yellow-500 bg-yellow-50 hover:bg-yellow-100 text-green-950 transition"
                        >
                          {product.external_button_text || 'Buy on Amazon'} ↗
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Product Quick-View & Multi-Image Gallery Modal */}
      {quickViewProduct && (
        <div
          className="fixed inset-0 z-50 bg-gray-900/70 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="quick-view-modal-title"
          data-testid="product-quick-view-modal"
        >
          <div className="bg-white rounded-2xl max-w-4xl w-full shadow-2xl border border-gray-200 overflow-hidden my-8">
            <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-green-800 to-green-700 text-white">
              <h3 id="quick-view-modal-title" className="text-lg font-extrabold truncate">
                {quickViewProduct.name}
              </h3>
              <button
                type="button"
                onClick={() => setQuickViewProduct(null)}
                className="p-1.5 rounded-full bg-white/15 hover:bg-white/25 text-white cursor-pointer"
                aria-label="Close product gallery modal"
                data-testid="close-quick-view-btn"
              >
                ✕
              </button>
            </div>

            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left: Gallery Viewer */}
              <div>
                {(() => {
                  const gallery =
                    Array.isArray(quickViewProduct.gallery_images) && quickViewProduct.gallery_images.length > 0
                      ? quickViewProduct.gallery_images
                      : [quickViewProduct.primary_image_url];
                  const activeImg = gallery[quickViewImageIdx] || gallery[0];
                  return (
                    <>
                      <div className="h-72 sm:h-80 rounded-xl bg-green-50/70 border border-gray-200 flex items-center justify-center overflow-hidden mb-3">
                        <img
                          src={activeImg}
                          alt={quickViewProduct.name}
                          className="max-h-full max-w-full object-contain p-4"
                        />
                      </div>
                      {gallery.length > 1 && (
                        <div className="flex items-center gap-2 overflow-x-auto pb-1">
                          {gallery.map((img, i) => (
                            <button
                              key={i}
                              type="button"
                              onClick={() => setQuickViewImageIdx(i)}
                              className={`w-16 h-16 rounded-lg overflow-hidden border-2 flex-shrink-0 cursor-pointer ${
                                quickViewImageIdx === i ? 'border-green-600' : 'border-gray-200 opacity-70'
                              }`}
                            >
                              <img
                                src={img}
                                alt={`${quickViewProduct.name} view ${i + 1}`}
                                loading="lazy"
                                fetchpriority="low"
                                className="w-full h-full object-cover"
                              />
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* Right: Full Product Info & Add to Cart */}
              <div className="flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-bold uppercase px-2.5 py-0.5 rounded-full bg-green-100 text-green-800">
                      {quickViewProduct.category.replace('_', ' ')}
                    </span>
                    {quickViewProduct.sku && (
                      <span className="text-xs text-gray-400">SKU: {quickViewProduct.sku}</span>
                    )}
                  </div>

                  <div className="text-2xl font-extrabold text-green-900 mb-3">
                    {formatPrice(getEffectiveUnitPrice(quickViewProduct, getProductVariant(quickViewProduct)))}
                  </div>

                  <div className="text-sm text-gray-700 whitespace-pre-line leading-relaxed mb-5 max-h-48 overflow-y-auto pr-1">
                    {quickViewProduct.description || quickViewProduct.short_description}
                  </div>

                  {Array.isArray(quickViewProduct.variants) &&
                    quickViewProduct.variants.map((vg, idx) => (
                      <div key={idx} className="mb-4">
                        <label className="block text-xs font-bold text-gray-700 mb-1.5">{vg.name}:</label>
                        <div className="flex flex-wrap gap-2">
                          {(vg.options || []).map((opt) => (
                            <button
                              key={opt}
                              type="button"
                              onClick={() => handleVariantChange(quickViewProduct.id, opt)}
                              className={`text-xs px-3 py-1.5 rounded-lg font-semibold border cursor-pointer ${
                                getProductVariant(quickViewProduct) === opt
                                  ? 'bg-green-700 text-white border-green-700'
                                  : 'bg-gray-50 text-gray-700 border-gray-300'
                              }`}
                            >
                              {opt}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}

                  <div className="flex items-center gap-3 mb-5">
                    <label className="text-xs font-bold text-gray-700">Quantity:</label>
                    <div className="inline-flex items-center border border-gray-300 rounded-lg">
                      <button
                        type="button"
                        onClick={() => setQuickViewQty((q) => Math.max(1, q - 1))}
                        className="px-3 py-1 text-gray-700 hover:bg-gray-100 font-bold cursor-pointer"
                      >
                        −
                      </button>
                      <span className="px-4 py-1 text-sm font-bold">{quickViewQty}</span>
                      <button
                        type="button"
                        onClick={() => setQuickViewQty((q) => Math.min(25, q + 1))}
                        className="px-3 py-1 text-gray-700 hover:bg-gray-100 font-bold cursor-pointer"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => {
                      const prod = quickViewProduct;
                      setQuickViewProduct(null);
                      handleAddProduct(prod, quickViewQty, true);
                    }}
                    data-testid="quick-view-add-to-cart-btn"
                    className="flex-1 bg-yellow-400 hover:bg-yellow-500 text-green-950 font-extrabold py-3 px-5 rounded-xl shadow-md transition cursor-pointer"
                  >
                    🛒 Add {quickViewQty} to Cart
                  </button>
                  <button
                    type="button"
                    onClick={() => setQuickViewProduct(null)}
                    className="px-5 py-3 rounded-xl border border-gray-300 text-gray-700 font-semibold text-sm hover:bg-gray-50 cursor-pointer"
                  >
                    Continue Shopping
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExtrasShopPage;
